import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../../../../api';
import Pagination from '../../../../Tools/Pagination';

const VehicleChecklist = ({ formData, onChange, onSave, editId, vehicleId }) => {
  const [checklistItems, setChecklistItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const currentVehicleId = vehicleId || sessionStorage.getItem('currentVehicleId');
  const authToken = sessionStorage.getItem('auth_token');
  const userId = sessionStorage.getItem('user_id');

  const handleModalClose = () => {
    sessionStorage.removeItem('currentVehicleId');
  };

  /* --------------------------------------------------------------
     FETCH CHECKLIST – now stores vehicle_checklist_id
     -------------------------------------------------------------- */
  useEffect(() => {
    const fetchChecklist = async () => {
      if (!currentVehicleId) return;

      try {
        const response = await axiosInstance.get(
          `/ems/start-vehicle-checklist/${currentVehicleId}`,
          {
            headers: {
              Accept: 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        const rawItems = response.data?.data ?? [];

        const formattedItems = rawItems.map((item) => ({
          rowId: item.id,                                          // keep row id for UI keys
          id: item.vehicle_checklist_id ?? item.id,                // <-- real checklist id for payload
          name: item.name,
          criteria: item.criteria.toString(),
          children:
            item.ems_child_vehicle_checklist?.map((child) => ({
              rowId: child.id,
              id: child.vehicle_checklist_id ?? child.id,          // <-- child checklist id
              name: child.name,
              criteria: child.criteria.toString(),
            })) ?? [],
        }));

        formattedItems.sort((a, b) => a.rowId - b.rowId);
        setChecklistItems(formattedItems);
        setCurrentPage(1);
      } catch (error) {
        console.error('Error fetching checklist items:', error);
      }
    };

    fetchChecklist();

    const modalEl = document.getElementById('bs-daily-checklist');
    if (modalEl) {
      modalEl.addEventListener('hidden.bs.modal', handleModalClose);
    }

    return () => {
      if (modalEl) {
        modalEl.removeEventListener('hidden.bs.modal', handleModalClose);
      }
    };
  }, [currentVehicleId, authToken]);

  /* --------------------------------------------------------------
     CHECKBOX LOGIC (unchanged – uses rowId)
     -------------------------------------------------------------- */
  const handleCheckboxChange = (e, parentItem, childItem = null) => {
    const { name, checked } = e.target;
    const updatedFormData = { ...formData, [name]: checked };

    if (!childItem) {
      parentItem.children.forEach((child) => {
        updatedFormData[`check-id-${child.rowId}`] = checked;
      });
    } else {
      const allChecked = parentItem.children.every((c) => {
        const key = `check-id-${c.rowId}`;
        return key === name ? checked : formData[key];
      });
      updatedFormData[`check-id-${parentItem.rowId}`] = allChecked;
    }

    onChange({
      target: {
        name,
        value: checked,
        type: 'checkbox',
        newFormData: updatedFormData,
      },
    });
  };

  /* --------------------------------------------------------------
     SUBMIT – uses vehicle_checklist_id & omits empty children
     -------------------------------------------------------------- */
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (!authToken) throw new Error('Authentication required');
      if (!userId) throw new Error('User ID not found');
      if (!currentVehicleId) throw new Error('Vehicle ID is required');

      const checklistData = checklistItems.map((item) => {
        const itemKey = `id-${item.rowId}`;
        const base = {
          id: item.id,                                           // <-- vehicle_checklist_id
          status: formData[`check-${itemKey}`] ? 1 : 0,
          notes: formData[`notes-${itemKey}`] || '',
        };

        if (item.children && item.children.length > 0) {
          base.children = item.children.map((child) => {
            const childKey = `id-${child.rowId}`;
            return {
              id: child.id,                                     // <-- child vehicle_checklist_id
              status: formData[`check-${childKey}`] ? 1 : 0,
              notes: formData[`notes-${childKey}`] || '',
            };
          });
        }

        return base;
      });

      const payload = {
        user_id: parseInt(userId, 10),
        vehicle_id: parseInt(currentVehicleId, 10),
        checklist: checklistData,
      };

      console.log("Payload →", JSON.stringify(payload, null, 2));

      await axiosInstance.post('/ems/submit-vehicle-checklist', payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      alert('Checklist submitted successfully');
      const modalEl = document.getElementById('bs-daily-checklist');
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      modal.hide();
      sessionStorage.removeItem('currentVehicleId');
    } catch (error) {
      console.error('Error submitting checklist:', error);
      alert(`Failed to submit checklist: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePageChange = (e, page) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentPage(page);
  };

  const totalItems = checklistItems.length;
  const paginatedItems = checklistItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderChecklistItems = (items) => {
    return items.map((item, index) => {
      const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
      const itemKey = `id-${item.rowId}`;

      return (
        <React.Fragment key={`item-${item.rowId}`}>
          {/* Parent Row */}
          <tr>
            <td>{serialNumber}</td>
            <td>
              <p className="mb-0 fw-normal">{item.name}</p>
            </td>
            <td>
              <input
                type="text"
                className="form-control"
                value={item.criteria}
                disabled
              />
            </td>
            <td>
              <input
                type="checkbox"
                className="form-check-input"
                name={`check-${itemKey}`}
                checked={formData[`check-${itemKey}`] || false}
                onChange={(e) => handleCheckboxChange(e, item, null)}
              />
            </td>
            <td>
              <textarea
                className="form-control"
                name={`notes-${itemKey}`}
                value={formData[`notes-${itemKey}`] || ''}
                onChange={onChange}
              />
            </td>
          </tr>

          {/* Children */}
          {item.children.map((child) => {
            const childKey = `id-${child.rowId}`;
            return (
              <tr key={`child-${child.rowId}`} className="child-row">
                <td></td>
                <td className="ps-4">
                  <p className="mb-0 fw-normal">↳ {child.name}</p>
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={child.criteria}
                    disabled
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name={`check-${childKey}`}
                    checked={formData[`check-${childKey}`] || false}
                    onChange={(e) => handleCheckboxChange(e, item, child)}
                  />
                </td>
                <td>
                  <textarea
                    className="form-control"
                    name={`notes-${childKey}`}
                    value={formData[`notes-${childKey}`] || ''}
                    onChange={onChange}
                  />
                </td>
              </tr>
            );
          })}
        </React.Fragment>
      );
    });
  };

  return (
    <div
      id="bs-daily-checklist"
      className="modal fade"
      tabIndex="-1"
      aria-labelledby="bs-daily-checklist"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-xl">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">Daily Checklist</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12">
                <table className="table text-nowrap customize-table mb-0 align-middle">
                  <thead className="text-dark">
                    <tr>
                      <th>ID</th>
                      <th>Item</th>
                      <th>Criteria</th>
                      <th>Check</th>
                      <th>Notes (If Any)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistItems.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          No Checklist Items Found
                        </td>
                      </tr>
                    ) : (
                      renderChecklistItems(paginatedItems)
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {checklistItems.length > itemsPerPage && (
              <div className="mt-3">
                <Pagination
                  total={totalItems}
                  limit={itemsPerPage}
                  page={currentPage}
                  onPageChange={(page) => handlePageChange(new Event('click'), page)}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-danger"
              data-bs-dismiss="modal"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Checklist'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleChecklist;